import { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: any | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (data: any) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session?.user?.id || 'No user');
        
        if (!mounted) return;

        // Update state immediately
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Defer profile fetching to avoid blocking auth state change
          setTimeout(() => {
            if (mounted) {
              fetchProfile(session.user.id);
            }
          }, 0);
        } else {
          setProfile(null);
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session error:', error);
          if (mounted) {
            setLoading(false);
          }
          return;
        }

        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
          
          if (session?.user) {
            await fetchProfile(session.user.id);
          }
          
          setLoading(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      console.log('Fetching profile for user:', userId);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Profile fetch error:', error);
        throw error;
      }

      console.log('Profile fetched:', data ? 'Found' : 'Not found', data);
      setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Sign in error:', error);
        toast({
          title: "Sign In Failed",
          description: error.message,
          variant: "destructive"
        });
        setLoading(false);
        throw error;
      }

      if (data.user) {
        toast({
          title: "Welcome back!",
          description: "You have successfully signed in.",
        });

        console.log('Sign-in successful, redirecting to dashboard');
      }

    } catch (error: any) {
      console.error('Sign in error:', error);
      setLoading(false);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, displayName?: string) => {
    try {
      console.log('Starting signup process for:', email);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            display_name: displayName || null
          }
        }
      });

      if (error) {
        console.error('Signup error:', error);
        toast({
          title: "Sign Up Failed",
          description: error.message,
          variant: "destructive"
        });
        throw error;
      }

      if (!data.user) {
        throw new Error('User creation failed - no user returned');
      }

      console.log('User created successfully:', data.user.id);

      if (displayName) {
        try {
          const { error: profileError } = await supabase.from('profiles').insert({
            user_id: data.user.id,
            display_name: displayName,
            email: email,
            company_name: null,
            phone: null,
            wallet_address: null,
            organization_type: null,
            business_sector: null,
            location: null,
            description: null
          });
          
          if (profileError) {
            console.error('Profile creation failed:', profileError);
            toast({
              title: "Account Created",
              description: "Account created but profile setup incomplete. You can update your profile later.",
              variant: "default"
            });
            return;
          }
          
          console.log('Profile created successfully with extended fields');
        } catch (profileError) {
          console.error('Profile creation error:', profileError);
        }
      }

      toast({
        title: "Account Created Successfully",
        description: data.user.email_confirmed_at 
          ? "Your account is ready to use!" 
          : "Please check your email to verify your account.",
      });

    } catch (error: any) {
      console.error('Signup process failed:', error);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        toast({
          title: "Google Sign In Failed",
          description: error.message,
          variant: "destructive"
        });
        throw error;
      }

    } catch (error: any) {
      console.error('Google sign in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      
      // Clear local state immediately
      setUser(null);
      setSession(null);
      setProfile(null);
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Sign out error:', error);
        // Still redirect even if there's an error
      }
      
      // Clear any stored tokens
      localStorage.removeItem('supabase.auth.token');
      
      // Redirect to auth page
      window.location.href = '/auth';

    } catch (error: any) {
      console.error('Sign out error:', error);
      // Force redirect even on error
      window.location.href = '/auth';
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (data: any) => {
    if (!user) {
      console.error('AuthProvider.updateProfile: No user found');
      return;
    }

    console.log('AuthProvider.updateProfile: Updating profile with data:', data);
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          email: user.email,
          ...data,
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('AuthProvider.updateProfile: Database error:', error);
        
        // If it's a role conflict, the profile might still have been created successfully
        if (error.code === '23505' && error.message?.includes('user_roles_user_id_role_key')) {
          console.log('Role conflict detected, checking if profile was created...');
          
          // Wait a moment for any ongoing transactions to complete
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Try to fetch the profile to see if it was created despite the role error
          try {
            await fetchProfile(user.id);
            console.log('Profile creation succeeded despite role conflict');
            toast({
              title: "Welcome!",
              description: "Your account has been set up successfully.",
            });
            return; // Success - profile was created
          } catch (fetchError) {
            console.error('Profile was not created due to role conflict');
          }
        }
        
        toast({
          title: "Update Failed",
          description: error.message,
          variant: "destructive"
        });
        throw error;
      }

      console.log('AuthProvider.updateProfile: Profile updated successfully, fetching updated profile...');
      await fetchProfile(user.id);
      
      toast({
        title: "Profile Updated",
        description: "Your profile has been successfully updated.",
      });

    } catch (error: any) {
      console.error('AuthProvider.updateProfile: Update failed:', error);
      throw error;
    }
  };

  const value = {
    user,
    session,
    profile,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};